import { ColumnDef, flexRender, Table, ColumnResizeMode } from "@tanstack/react-table"
import EmptyState from "Components/organisms/empty-state"

interface DataTableProps<T> {
    table: Table<T>
    columns: ColumnDef<T>[],
    stickyHead?: boolean,
    stickyFoot?: boolean,
    columnResizeMode?: ColumnResizeMode
}

const DataTable = <T,>({
    table,
    columns,
    stickyHead = false,
    stickyFoot = false,
    columnResizeMode = 'onChange'
}: DataTableProps<T>) => {
    return (
        <div className="data-table-wrapper">
            <table
                className="data-table table align-middle"
                style={{ tableLayout: 'fixed', minWidth: '100%', width: table.getTotalSize() }}
            >
                <thead>
                    {table.getHeaderGroups().map((hg) => (
                        <tr key={hg.id}>
                            {hg.headers.map((header) => {
                                const meta = header.column.columnDef.meta as any;

                                const headerClassName = meta?.getHeaderClassName?.(header) ?? '';
                                // Click a column title to sort: asc -> desc -> none (useDataTable)
                                const canSort = !header.isPlaceholder && header.column.getCanSort();
                                const sorted = header.column.getIsSorted();
                                return (
                                    <th
                                        key={header.id}
                                        colSpan={header.colSpan}
                                        title={meta?.getHeaderTitle?.(header) ?? (canSort ? 'Click to sort' : undefined)}
                                        className={`${stickyHead ? 'sticky-header' : ''} ${canSort ? 'sortable-header' : ''} ${headerClassName}`.trim()}
                                        onClick={canSort ? header.column.getToggleSortingHandler() : undefined}
                                        aria-sort={sorted === 'asc' ? 'ascending' : sorted === 'desc' ? 'descending' : canSort ? 'none' : undefined}
                                        style={{
                                            position: stickyHead ? undefined : 'relative',
                                            width: header.getSize(),
                                            userSelect: header.column.getIsResizing() ? 'none' : 'auto',
                                            cursor: canSort ? 'pointer' : undefined,
                                            ...(meta?.getHeaderStyle?.() ?? {})
                                        }}
                                    >
                                        {header.isPlaceholder
                                            ? null
                                            : flexRender(header.column.columnDef.header, header.getContext())}
                                        {canSort && (
                                            <i
                                                aria-hidden
                                                className={`sort-indicator mdi ${sorted === 'asc' ? 'mdi-arrow-up' : sorted === 'desc' ? 'mdi-arrow-down' : 'mdi-swap-vertical is-idle'}`}
                                            />
                                        )}

                                        {!header.isPlaceholder && header.column.getCanResize() && (
                                            <div
                                                onClick={(e) => e.stopPropagation()}
                                                onMouseDown={header.getResizeHandler()}
                                                onTouchStart={header.getResizeHandler()}
                                                className={`col-resize-handle ${header.column.getIsResizing() ? 'is-resizing' : ''}`}
                                            />
                                        )}
                                    </th>
                                );
                            })}
                        </tr>
                    ))}
                </thead>

                <tbody>
                    {table.getRowModel().rows.length === 0 && (
                        <tr>
                            <td colSpan={table.getVisibleLeafColumns().length || columns.length} className="text-center text-muted">
                                <EmptyState />
                            </td>
                        </tr>
                    )}

                    {table.getRowModel().rows.map((row) => (
                        <tr key={row.id} className={row.subRows?.length ? 'is-group-row' : undefined}>
                            {row.getVisibleCells().map((cell: any) => {
                                const styleFn = cell.column.columnDef.meta?.getCellStyle;
                                const style = styleFn ? styleFn(cell) : {};
                                const cellClassName = cell.column.columnDef.meta?.getCellClassName?.(cell.row.original) ?? '';
                                return (
                                    <td
                                        className={`${cell.column.columnDef.meta?.cellClassName ?? ''} ${cellClassName}`.trim()}
                                        title={cell.column.columnDef.meta?.getCellTitle?.(cell.row) ?? undefined}
                                        key={cell.id}
                                        style={{
                                            whiteSpace: "nowrap",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            ...style,
                                            width: cell.column.getSize(),
                                        }}
                                    >
                                        {flexRender(
                                            cell.column.columnDef.cell,
                                            cell.getContext()
                                        )}
                                    </td>
                                )
                            })}
                        </tr>
                    ))}
                </tbody>
                {
                    table.getFooterGroups().some(fg => fg.headers.some(h => !h.isPlaceholder && h.column.columnDef.footer)) &&
                    <tfoot>
                        {table.getFooterGroups().map(fg => (
                            <tr key={fg.id}>
                                {fg.headers.map(h => {
                                    const meta = h.column.columnDef.meta as any
                                    const footerClassName = meta?.getFooterClassName?.(h) ?? '';
                                    return (
                                        <td
                                            key={h.id}
                                            title={meta?.getFooterTitle?.(h) ?? undefined}
                                            className={`${stickyFoot ? 'sticky-footer' : ''} ${footerClassName}`.trim()}
                                            style={{ ...meta?.getFooterStyle?.() }}
                                        >
                                            {h.isPlaceholder ? null : flexRender(h.column.columnDef.footer, h.getContext())}
                                        </td>
                                    )
                                })}
                            </tr>
                        ))}
                    </tfoot>
                }
            </table>
        </div>
    )
}

export default DataTable